//#region types
import { FormEnums } from "models/enums";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import {
  AutoCompleteExtraProps,
  GenericFormSection,
  MonthRangeFormFieldProps,
} from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelWintSalaryDeviations } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCompanyService, useWintSalaryDeviationService, useWintSalaryService } from "serviceHooks";
import { useDeviationPermissions } from "components/salaryDeviation";
//#endregion
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { DateHelper, FilterHelper, QueryStateHelper } from "utils/helpers";

function useWintSalaryDeviationReportFilterDefinition() {
  const companyService = useCompanyService();
  const { searchPersonsAutoComplete } = useWintSalaryService();
  const { getValidDeviationCategoriesSelectionItems, getAllDeviationTypes, searchDeviationTypes } =
    useWintSalaryDeviationService();

  const { canManageSalary, isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } =
    useDeviationPermissions();

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModelWintSalaryDeviations.DeviationReportFilter
  ): Promise<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter> => {
    const reportFilter: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter = {
      yearMonthRange: {},
    };

    if (filter.personExternalIds?.length) {
      const persons = await companyService.getEmployees({ personIds: filter.personExternalIds });
      reportFilter.persons = persons.map((person) => ({ id: person.id, value: person.name ?? "" }));
    }

    if (filter.salaryDeviationTypeIds?.length) {
      const types = await getAllDeviationTypes();
      reportFilter.deviationTypes = types.filter((type) => filter.salaryDeviationTypeIds?.includes(type.id as string));
    }

    if (filter.deviationCategories?.length) {
      reportFilter.categoryTypes = getValidDeviationCategoriesSelectionItems().filter((category) =>
        filter.deviationCategories?.includes(
          category.id as WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory
        )
      );
    }

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.YearMonthRange,
      filter.startPeriod ? DateHelper.getDateFromYearMonth(filter.startPeriod) : undefined,
      filter.endPeriod ? DateHelper.getDateFromYearMonth(filter.endPeriod) : undefined
    );

    return reportFilter;
  };

  const filterFormFileds: GenericFormSection<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        yearMonthRange: {
          label: TranslationKeys.Common.TimePeriod,
          type: FormEnums.FormFieldType.MONTHRANGE,
          hideInReadOnlyRemovableView: true,
          extraProps: {
            hideClearable: true,
          } as MonthRangeFormFieldProps,
        },
        persons: {
          label: TranslationKeys.Common.Employee,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.Persons,
            items: (searchStr: string) =>
              searchPersonsAutoComplete(
                searchStr,
                false,
                true,
                isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
              ),
          } as AutoCompleteExtraProps<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>,
          hidden: !canManageSalary,
        },
        deviationTypes: {
          label: TranslationKeys.SalaryDeviation.DeviationTypeLabel,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.DeviationTypes,
            items: (searchStr: string) => searchDeviationTypes(searchStr),
          } as AutoCompleteExtraProps<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>,
        },
        categoryTypes: {
          label: TranslationKeys.SalaryDeviation.DeviationCategory,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: getValidDeviationCategoriesSelectionItems().map((item) => ({
              id: item.id,
              value: item.value,
            })),
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter> = {
    persons: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.Persons
    ),
    categoryTypes: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.CategoryTypes,
      getValidDeviationCategoriesSelectionItems()
    ),
    deviationTypes: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.DeviationTypes
    ),
    yearMonthRange: QueryStateHelper.monthRangeQueryEncoder(
      FormFieldConstants.SalaryDeviation.WintSalaryDeviationReportFilter.YearMonthRange
    ),
  };

  const onClickClearAll = (
    values: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter,
    setValues: (values: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter) => void
  ) => {
    setValues({
      yearMonthRange: values.yearMonthRange,
    });
  };

  return { filterFormFileds, filterQueryStateHandler, apiToReportFilterFormatter, onClickClearAll };
}

export default useWintSalaryDeviationReportFilterDefinition;
